import { Phone, MessageCircle, Mail, MapPin, Instagram, Facebook } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useSiteSettings } from "@/hooks/useSiteSettings";

export default function ContactsPage() {
  const settings = useSiteSettings();
  const wa = settings.whatsapp.replace(/\D/g, "");
  const tel = settings.phone.replace(/\s/g, "");

  const CHANNELS = [
    { icon: Phone, label: "Telefone", value: settings.phone, href: `tel:${tel}`, testid: "contact-phone" },
    { icon: MessageCircle, label: "WhatsApp", value: settings.phone, href: `https://wa.me/${wa}`, testid: "contact-whatsapp" },
    { icon: Mail, label: "Email", value: settings.email, href: `mailto:${settings.email}`, testid: "contact-email" },
    { icon: MapPin, label: "Morada", value: settings.address, href: null, testid: "contact-address" },
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="contacts-page">
      <Header />
      <main className="pt-24 pb-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <p className="text-xs font-semibold tracking-[0.35em] uppercase text-slate-500 mb-4">Fale Connosco</p>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight text-[#0072C6]" data-testid="contacts-heading">Contactos</h1>
          <p className="mt-5 text-slate-600 max-w-2xl">Estamos disponíveis para o ajudar. Escolha a forma de contacto que preferir.</p>

          <div className="mt-12 grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5 space-y-4">
              {CHANNELS.map(({ icon: Icon, label, value, href, testid }) => {
                const Inner = (
                  <>
                    <span className="w-12 h-12 flex items-center justify-center bg-[#0072C6] text-white shrink-0">
                      <Icon className="w-5 h-5" />
                    </span>
                    <div>
                      <p className="text-xs uppercase tracking-wider text-slate-500">{label}</p>
                      <p className="font-semibold text-[#0072C6]">{value}</p>
                    </div>
                  </>
                );
                return href ? (
                  <a key={label} href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer"
                    data-testid={testid} className="flex items-center gap-4 bg-[#F8FAFC] border border-slate-200 p-6 hover:border-[#0072C6]/40 hover:shadow-sm transition-all">
                    {Inner}
                  </a>
                ) : (
                  <div key={label} data-testid={testid} className="flex items-center gap-4 bg-[#F8FAFC] border border-slate-200 p-6">
                    {Inner}
                  </div>
                );
              })}

              <div className="flex gap-3 pt-2" data-testid="contacts-socials">
                <a href={settings.instagram} target="_blank" rel="noopener noreferrer" data-testid="contacts-instagram" aria-label="Instagram"
                  className="w-12 h-12 flex items-center justify-center border border-slate-200 text-slate-600 hover:bg-[#0072C6] hover:text-white hover:border-[#0072C6] transition-colors">
                  <Instagram className="w-5 h-5" />
                </a>
                <a href={settings.facebook} target="_blank" rel="noopener noreferrer" data-testid="contacts-facebook" aria-label="Facebook"
                  className="w-12 h-12 flex items-center justify-center border border-slate-200 text-slate-600 hover:bg-[#0072C6] hover:text-white hover:border-[#0072C6] transition-colors">
                  <Facebook className="w-5 h-5" />
                </a>
              </div>
            </div>

            <div className="lg:col-span-7">
              <div className="bg-[#F8FAFC] border border-slate-200 h-[320px] sm:h-[420px] flex items-center justify-center" data-testid="contacts-map">
                {settings.map_embed_url ? (
                  <iframe title="Mapa" src={settings.map_embed_url} className="w-full h-full border-0" loading="lazy" allowFullScreen />
                ) : (
                  <div className="text-center px-6">
                    <MapPin className="w-10 h-10 text-[#0072C6]/30 mx-auto mb-3" />
                    <p className="text-sm text-slate-500">Mapa de localização</p>
                    <p className="text-xs text-slate-400 mt-1">O administrador pode adicionar o mapa nas Definições.</p>
                  </div>
                )}
              </div>
              <div className="mt-8 bg-white border border-slate-200 p-7" data-testid="contacts-form-cta">
                <h2 className="font-display text-2xl text-[#0072C6] mb-3">Formulário de contacto</h2>
                <p className="text-sm text-slate-600 mb-6">Prefere escrever? Use o nosso formulário na página principal.</p>
                <a href="/#contacto" data-testid="contacts-form-link"
                  className="inline-flex items-center bg-[#0072C6] text-white font-semibold px-8 py-4 text-sm hover:bg-[#005FA3] transition-colors">
                  Ir para o formulário
                </a>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
